import React from 'react';
import V2Layout from '../Layout';
import { Grid, Button, TableContainer, Table, TableHead, TableRow, TableCell, TableSortLabel, TableBody, TablePagination, Card, IconButton, TextField, InputAdornment, Box, FormGroup, FormControlLabel, Checkbox, MenuItem } from '@mui/material';
import { translate } from '../../../i18n/customI18nProvider';
import StyledTableRow from '../../../components/v2/common/table/StyledTableRow';
import { getWristbandCategoryList, deleteWristbandCategory, hideWristbandCategory, unhideWristbandCategory } from '../../../services/wristbandCategory.service';
import EditIcon from '@mui/icons-material/Edit';
import { visuallyHidden } from '@mui/utils';
import DeleteIcon from '@mui/icons-material/Delete';
import ShowIcon from '@mui/icons-material/Visibility';
import HideIcon from '@mui/icons-material/HideSource';
import UnhideIcon from '@mui/icons-material/SettingsBackupRestore';
import SearchIcon from '@mui/icons-material/Search';
import Swal from 'sweetalert2';
import config from '../../../config';
import { removeUrlParam, setUrlParam } from '../../../services/common.service';
import { booleanToChip, optionsToChip } from '../../../util';

const tableHeader = [
    {
        id: 'name',
        key: 'name',
        align: 'left',
        disablePadding: false,
        label: translate('custom.name'),
        sortable: true
    },
    {
        id: 'type',
        key: 'type',
        align: 'left',
        disablePadding: false,
        label: translate('custom.type'),
        sortable: true
    },
    {
        id: 'custom',
        key: 'custom',
        align: 'left',
        disablePadding: false,
        label: translate('custom.custom'),
        sortable: true
    },
    {
        id: 'quantity',
        key: 'quantity',
        align: 'left',
        disablePadding: false,
        label: translate('custom.quantity'),
        sortable: true
    },
    {
        id: 'actions',
        key: 'actions',
        align: 'left',  
        disablePadding: false,
        label: translate('custom.actions'),
        sortable: false
    }
]

const colors = {
    paper: 'primary',
    fabric: 'warning',
}

class Wristbands extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            count: 0,
            skip: 0,
            limit: 50,
            limitOptions: [10, 50, 100, 250, 500],
            sort_key: 'name',
            sort_order: 'ASC',
            search: '',
            uid: '',
            custom: 'both',
            type: 'all',
            showHidden: false,
            categories: [],
        };
    }

    async setStateAsync(state) {
        return new Promise((resolve) => {
            this.setState(state, resolve);
        });
    }

    async componentDidMount() {
        const urlParams = new URLSearchParams(window.location.search);
        const search = urlParams.get('search') ? urlParams.get('search').replaceAll('"', '') : '';
        const showHidden = urlParams.get('showHidden') === 'true' ? true : false;
        const type = urlParams.get('type') ? urlParams.get('type').replaceAll('"', '') : 'all';
        const custom = urlParams.get('custom') ? urlParams.get('custom').replaceAll('"', '') : 'both';
        const uid = urlParams.get('uid') ? urlParams.get('uid').replaceAll('"', '') : '';

        await this.setStateAsync({
            search: search,
            showHidden: showHidden,
            type: type,
            custom: custom,
            uid: uid
        });

        this.getCategories();
    }

    async handleDeleteWristbandCategory(category_id) {
        const confirm_delete = await Swal.fire({
            icon: 'warning',
            title: translate('custom.warning'),
            text: translate('custom.are_you_sure_you_want_to_delete') + ' ' + translate('custom.you_will_not_be_able_to_undo_this_action'),
            showCancelButton: true,
            confirmButtonText: translate('custom.delete'),
            cancelButtonText: translate('custom.cancel'),
            confirmButtonColor: config.baseColor
        });
        if (confirm_delete.isConfirmed) {
            let result = await deleteWristbandCategory(category_id);
            if (result.error) {
                Swal.fire({
                    icon: 'error',
                    title: translate('custom.error'),
                    text: result.error.error ? result.error.error : ''
                });
                return;
            }

            Swal.fire({
                icon: 'success',
                title: translate('custom.success'),
                text: translate('custom.deleted_successfully')
            });

            this.getCategories();
        }
    }

    async handleHideWristbandCategory(category_id) {
        const result = await hideWristbandCategory(category_id);
        if (result.error) {
            Swal.fire({
                icon: 'error',
                title: translate('custom.error'),
                text: result.error.error ? result.error.error : ''
            });
            return;
        }

        Swal.fire({
            icon: 'success',
            title: translate('custom.success'),
            text: translate('custom.hide_successfully')
        });

        this.getCategories();
    }

    async handleUnhideWristbandCategory(category_id) {
        const result = await unhideWristbandCategory(category_id);
        if (result.error) {
            Swal.fire({
                icon: 'error',
                title: translate('custom.error'),
                text: result.error.error ? result.error.error : ''
            });
            return;
        }

        Swal.fire({
            icon: 'success',
            title: translate('custom.success'),
            text: translate('custom.unhide_successfully')
        });

        this.getCategories();
    }

    async getCategories() {
        let options = {
            skip: this.state.skip,
            limit: this.state.limit
        }
        
        if (this.state.sort_key) {
            options.sort = encodeURIComponent(JSON.stringify([this.state.sort_key, this.state.sort_order]));
        }

        let filter = {};

        if (this.state.name) {
            filter.name = this.state.name;
        }
        if (!this.state.showHidden) {
            filter.hidden = false;
        }
        if (this.state.type && this.state.type != 'all') {
            filter.type = this.state.type;
        }
        if (this.state.custom && this.state.custom != 'both') {
            filter.custom = this.state.custom === 'custom' ? true : false;
        }
        if (this.state.uid) {
            filter['packets.wristbands.uid'] = this.state.uid;
        }


        options.filter =  encodeURIComponent(JSON.stringify(filter));

        let { categories, total } = await getWristbandCategoryList(options);

        this.setState({
            categories: categories,
            count: total
        });
    }

    render() {
        return (
            <V2Layout
                currentMenu='wristbands'
            >
                <Grid container spacing={2} style={{marginBottom: '10px', justifyContent: 'space-between'}}>
                    <Grid item>
                        <div style={{display: 'flex', gap: '20px'}}>
                            <TextField
                                size='small'
                                variant='outlined'
                                id="search-input"
                                label={translate('custom.name')}
                                placeholder={translate('custom.search')}
                                value={this.state.name}
                                onChange={async (e) => {
                                    await this.setStateAsync({name: e.target.value});
                                    (e?.target?.value?.length > 0) ? setUrlParam('name', e.target.value) : removeUrlParam('name');
                                    this.getCategories();
                                }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,

                                }}
                            />
                            <TextField
                                select
                                size='small'
                                variant='outlined'
                                label={translate('custom.type')}
                                value={this.state.type}
                                onChange={async (e) => {
                                    await this.setStateAsync({ type: e.target.value });
                                    (e?.target?.value && e.target.value != 'all') ? setUrlParam('type', e.target.value) : removeUrlParam('type');
                                    this.getCategories();
                                }}
                            >
                                <MenuItem key={0} value={"all"}>
                                    {translate('custom.all')}
                                </MenuItem>
                                <MenuItem key={1} value={"paper"}>
                                    {translate('custom.paper')}
                                </MenuItem>
                                <MenuItem key={2} value={"fabric"}>
                                    {translate('custom.fabric')}
                                </MenuItem>
                            </TextField>
                            <TextField
                                select
                                size='small'
                                variant='outlined'
                                label={translate('custom.custom')}
                                value={this.state.custom}
                                onChange={async (e) => {
                                    await this.setStateAsync({ custom: e.target.value });
                                    (e?.target?.value && e.target.value != 'both') ? setUrlParam('custom', e.target.value) : removeUrlParam('custom');
                                    this.getCategories();
                                }}
                            >
                                <MenuItem key={0} value={"both"}>
                                    {translate('custom.both')}
                                </MenuItem>
                                <MenuItem key={1} value={"standard"}>
                                    {translate('custom.standard')}
                                </MenuItem>
                                <MenuItem key={2} value={"custom"}>
                                    {translate('custom.custom')}
                                </MenuItem>
                            </TextField>

                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!this.state.showHidden}
                                            onChange={async (e) => {
                                                await this.setStateAsync({ showHidden: e.target.checked });
                                                (e.target.checked) ? setUrlParam('showHidden', true) : removeUrlParam('showHidden');
                                                this.getCategories();
                                            }}
                                        />
                                    }
                                    label={translate('custom.show_hidden')}
                                />
                            </FormGroup>
                            <TextField
                                size='small'
                                variant='outlined'
                                id="search-uid-input"
                                label={translate('custom.uid')}
                                placeholder={translate('custom.search')}
                                value={this.state.uid}
                                onChange={async (e) => {
                                    await this.setStateAsync({uid: e.target.value});
                                    (e?.target?.value?.length > 0) ? setUrlParam('uid', e.target.value) : removeUrlParam('uid');
                                    this.getCategories();
                                }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,

                                }}
                            />
                        </div>
                    </Grid>
                    <Grid item>
                        <Button variant='contained' style={{textTransform: 'none'}}
                            onClick={() => {
                                this.props.history.push('/v2/admin/wristbands/add');
                            }}
                        >
                            + {translate('custom.category')}
                        </Button>
                    </Grid>
                </Grid>
                
                <TableContainer component={Card} style={{maxHeight: '80vh', overflow: 'auto'}}>
                    <Table stickyHeader size='small'>
                        <TableHead>
                            <TableRow>
                                {tableHeader.map((headCell) => (
                                    <TableCell
                                        key={headCell.key}
                                        align={headCell.align ? headCell.align : 'left'}
                                        padding={headCell.disablePadding ? 'none' : 'normal'}
                                        sortDirection={(this.state.sort_key === headCell.key) ? ((this.state.sort_order == 'ASC') ? 'asc' : 'desc') : false}
                                    >
                                        {(headCell.sortable) ? (
                                            <TableSortLabel
                                                active={this.state.sort_key === headCell.key}
                                                direction={this.state.sort_key === headCell.key ? ((this.state.sort_order == 'ASC') ? 'asc' : 'desc') : 'asc'}
                                                onClick={async () => {
                                                    await this.setStateAsync({
                                                        sort_key: headCell.key,
                                                        sort_order: this.state.sort_key === headCell.key ? (this.state.sort_order === 'ASC' ? 'DESC' : 'ASC') : 'ASC'
                                                    })
                                                    this.getCategories();
                                                }}
                                            >
                                                {headCell.label}
                                                {this.state.sort_key === headCell.key ? (
                                                    <Box component="span" sx={visuallyHidden}>
                                                        {this.state.sort_order === 'DESC' ? 'sorted descending' : 'sorted ascending'}
                                                    </Box>
                                                ) : null}
                                            </TableSortLabel>
                                        ) : (
                                            <>
                                                {headCell.label}
                                            </>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(this.state.categories && Array.isArray(this.state.categories)) ? this.state.categories.map((row) => (
                                    <StyledTableRow key={row._id} hover
                                        onDoubleClick={() => {
                                            this.props.history.push('/v2/admin/wristbands/' + row._id + '/show');
                                        }}
                                    >
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{optionsToChip(translate("custom." + row.type), colors[row.type])}</TableCell>
                                        <TableCell>{row.custom ? translate('custom.custom') : translate('custom.standard')}</TableCell>
                                        <TableCell>{row.quantity}</TableCell>
                                        <TableCell>
                                            <IconButton size='small' onClick={() => {
                                                this.props.history.push('/v2/admin/wristbands/' + row._id + '/show');
                                            }}>
                                                <ShowIcon />
                                            </IconButton>
                                            <IconButton size='small' onClick={() => {
                                                this.props.history.push('/v2/admin/wristbands/' + row._id);
                                            }}>
                                                <EditIcon />
                                            </IconButton>
                                            {
                                                (row.hidden === false) ?
                                                <IconButton size='small' onClick={() => {
                                                    this.handleHideWristbandCategory(row._id);
                                                }}>
                                                    <HideIcon />
                                                </IconButton>
                                                :
                                                <IconButton size='small' onClick={() => {
                                                    this.handleUnhideWristbandCategory(row._id);
                                                }}>
                                                    <UnhideIcon />
                                                </IconButton>
                                            }
                                            <IconButton size='small' onClick={() => {
                                                this.handleDeleteWristbandCategory(row._id);
                                            }}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </StyledTableRow>
                                ))
                            : 
                                <TableRow>
                                    <TableCell colSpan={tableHeader.length} align="center" style={{padding: '20px'}}>
                                        {translate('custom.no_data')}
                                    </TableCell>
                                </TableRow>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                <Grid container style={{width: '100%'}}>
                    <Grid item xs={12}> 
                        <TablePagination
                            component="div"
                            count={this.state.count}
                            page={this.state.skip / this.state.limit}
                            onPageChange={async (event, page) => {
                                await this.setState({
                                    skip: page * this.state.limit
                                })
                                this.getCategories();
                            }}
                            rowsPerPage={this.state.limit}
                            onRowsPerPageChange={async (event) => {
                                await this.setState({
                                    limit: event.target.value
                                })
                                this.getCategories();
                            }}
                            rowsPerPageOptions={this.state.limitOptions}
                        />
                    </Grid>
                </Grid>
            </V2Layout>
        );
    }
}

export default Wristbands;